/**
 * @file     FinancialInsights.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  Listing-data and calculated ownership-cost summary above the mortgage calculator.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { hairline, hubColors, microLabelSx } from '@/components/hub/hubKitStyles';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { useTranslation } from 'react-i18next';
import { MORTGAGE_DEFAULTS } from '@/features/mortgage-calculator-flow/types';
import { calculateMortgageBreakdown, estimateAnnualPropertyTax, formatCAD, formatCADCents } from '@/utils/finance/mortgage';
import { POSTED_FIVE_YEAR_RATE_PCT } from '@/core/finance/mortgage';
import { useListingMortgageState } from './ListingMortgageState';

interface Props {
  listPrice: number;
  taxAnnualAmount: number | null;
  associationFee: number | null;
  /** Drop the heading and intro when a titled ListingSection wraps the panel. */
  headless?: boolean;
}

interface CostItem {
  label: string;
  value: string;
  source: string;
  emphasis?: boolean;
}

export function FinancialInsights({ listPrice, taxAnnualAmount, associationFee, headless = false }: Props) {
  const { t } = useTranslation();
  const state = useListingMortgageState();
  if (!state) return null;

  const downPayment = Math.round(listPrice * (state.downPct / 100));
  const annualTax = taxAnnualAmount ?? estimateAnnualPropertyTax(listPrice);
  const breakdown = calculateMortgageBreakdown({
    homePrice: listPrice,
    downPayment,
    annualInterestRate: state.rate,
    amortizationYears: state.amortization,
    annualPropertyTax: annualTax,
    annualInsurance: MORTGAGE_DEFAULTS.annualInsurance,
    monthlyHoa: associationFee ?? 0,
  });

  const listingData = t('propertyDetail.financials.listingData', { defaultValue: 'Listing data' });
  const estimate = t('propertyDetail.financials.estimate', { defaultValue: 'Calculated estimate' });
  const assumption = t('propertyDetail.financials.assumption', { defaultValue: 'Assumption' });
  const monthly: CostItem[] = [
    {
      label: t('propertyDetail.financials.monthlyTotal', { defaultValue: 'Estimated monthly total' }),
      value: formatCAD(breakdown.monthlyTotal),
      source: estimate,
      emphasis: true,
    },
    {
      label: t('propertyDetail.financials.principalInterest', { defaultValue: 'Principal and interest' }),
      value: formatCAD(breakdown.monthlyPrincipalAndInterest),
      source: estimate,
    },
    {
      label: t('propertyDetail.financials.propertyTax', { defaultValue: 'Property tax' }),
      value: `${formatCAD(breakdown.monthlyPropertyTax)}/mo`,
      source: taxAnnualAmount != null ? listingData : estimate,
    },
    ...(associationFee != null && associationFee > 0
      ? [{
          label: t('propertyDetail.financials.strataFee', { defaultValue: 'Strata fee' }),
          value: `${formatCADCents(associationFee)}/mo`,
          source: listingData,
        }]
      : []),
    {
      label: t('propertyDetail.financials.insurance', { defaultValue: 'Home insurance' }),
      value: `${formatCAD(breakdown.monthlyInsurance)}/mo`,
      source: assumption,
    },
  ];

  const upfront: CostItem[] = [
    {
      label: t('propertyDetail.financials.listPrice', { defaultValue: 'List price' }),
      value: formatCAD(listPrice),
      source: listingData,
    },
    {
      label: t('propertyDetail.financials.downPayment', { defaultValue: 'Down payment' }),
      value: `${formatCAD(downPayment)} (${Math.round(state.downPct)}%)`,
      source: estimate,
    },
    {
      label: t('propertyDetail.financials.mortgageBalance', { defaultValue: 'Estimated mortgage' }),
      value: formatCAD(breakdown.effectivePrincipal),
      source: estimate,
    },
    ...(breakdown.cmhcRequired
      ? [{
          label: t('propertyDetail.financials.cmhc', { defaultValue: 'CMHC premium' }),
          value: formatCAD(breakdown.cmhcPremium),
          source: estimate,
        }]
      : []),
  ];

  return (
    <Box component={headless ? 'div' : 'section'} aria-labelledby={headless ? undefined : 'financial-insights-heading'}>
      {headless ? (
        <Typography sx={{ mb: 5, fontSize: 15, color: hubColors.textSecondary, maxWidth: '68ch' }}>
          {state.rate === POSTED_FIVE_YEAR_RATE_PCT
            ? t('propertyDetail.financials.assumptionsPosted', {
                defaultValue: 'Uses the posted 5-year rate ({{rate}}%), {{down}}% down and a {{years}}-year amortization.',
                rate: state.rate,
                down: Math.round(state.downPct),
                years: state.amortization,
              })
            : t('propertyDetail.financials.assumptionsCustom', {
                defaultValue: 'Uses a {{rate}}% rate, {{down}}% down and a {{years}}-year amortization.',
                rate: state.rate,
                down: Math.round(state.downPct),
                years: state.amortization,
              })}
        </Typography>
      ) : (
        <>
          <Typography id="financial-insights-heading" variant="h4" component="h2" sx={{ fontWeight: 600 }}>
            {t('propertyDetail.financials.title', { defaultValue: 'Financial overview' })}
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary', maxWidth: 720 }}>
            {t('propertyDetail.financials.description', {
              defaultValue: 'Monthly and upfront costs for this home.',
            })}
          </Typography>
        </>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, columnGap: 8, rowGap: 6, mt: headless ? 0 : 3 }}>
        <CostGroup
          title={t('propertyDetail.financials.monthlyHeading', { defaultValue: 'Monthly ownership costs' })}
          items={monthly}
        />
        <CostGroup
          title={t('propertyDetail.financials.upfrontHeading', { defaultValue: 'Purchase snapshot' })}
          items={upfront}
        />
      </Box>

      <Typography sx={{ mt: 4, color: hubColors.textSecondary, fontSize: 13, lineHeight: 1.5 }}>
        {t('propertyDetail.financials.disclaimer', {
          defaultValue: 'Estimates only. They leave out utilities, upkeep and closing costs.',
        })}
      </Typography>
    </Box>
  );
}

function CostGroup({ title, items }: { title: string; items: CostItem[] }) {
  // The emphasised total closes the list, under the lines that make it up.
  const ordered = [...items.filter((item) => !item.emphasis), ...items.filter((item) => item.emphasis)];
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box component="h3" sx={{ ...microLabelSx, mb: 1 }}>{title}</Box>
      <Box component="dl" sx={{ m: 0 }}>
        {ordered.map((item) => (
          <Box
            key={item.label}
            title={item.source}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 4, minHeight: 44, py: 2.5, borderBottom: hairline }}
          >
            <Box
              component="dt"
              sx={{ m: 0, fontSize: 15, lineHeight: 1.45, color: item.emphasis ? hubColors.textPrimary : hubColors.textSecondary, fontWeight: item.emphasis ? fontWeightTokens.semibold : fontWeightTokens.regular }}
            >
              {item.label}
            </Box>
            <Box
              component="dd"
              sx={{ m: 0, flexShrink: 0, fontSize: 15, lineHeight: 1.45, fontWeight: item.emphasis ? fontWeightTokens.semibold : fontWeightTokens.medium, fontVariantNumeric: 'tabular-nums', color: hubColors.textPrimary }}
            >
              {item.value}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
